import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { FeatureFlagTypes } from '@raptor-fe/core';
import { ApplicationContextStore } from '../application-context/application-context.store';

/**
 * Builds a {@link CanActivateFn} that gates a route tree on a single tenant feature flag.
 *
 * This is a factory rather than a plain guard because the gated flag and the flag-off destination
 * differ per feature: each consumer route owns which {@link FeatureFlagTypes} value unlocks it and
 * where a tenant that lacks the flag should land. Closing over those two inputs lets every guarded
 * route reuse the same activation logic without a bespoke guard per feature.
 *
 * The `fallback` is supplied lazily as `() => string` because the named-route helpers it resolves
 * (e.g. `NamedRoutes.inspectionList().path`) are function calls; deferring the call keeps the path
 * resolved at activation time rather than at factory-construction time.
 *
 * The flag decision is deferred until {@link ApplicationContextStore} has finished its asynchronous
 * `onInit` (signalled by `isReady`). Without this wait the guard could read an empty `features()`
 * mid-initialisation and wrongly redirect a flag-enabled tenant to the fallback. When the store is
 * already ready the decision is returned synchronously; otherwise the guard returns an observable
 * that holds navigation until `isReady` emits true, then evaluates the flag exactly once.
 *
 * When the acting tenant has the flag enabled the route activates; otherwise the guard returns a
 * redirect {@link UrlTree} to the fallback path without imperatively navigating, so the guarded
 * route tree (and any data fetches its components would fire) never activates.
 *
 * @param flag The tenant feature flag that must be present for the route to activate.
 * @param fallback Lazily resolves the path to redirect to when the flag is absent.
 */
export const featureFlagGuard =
    (flag: FeatureFlagTypes, fallback: () => string): CanActivateFn =>
    (): boolean | UrlTree | Observable<boolean | UrlTree> => {
        const store = inject(ApplicationContextStore);
        const router = inject(Router);

        const evaluate = (): boolean | UrlTree => (store.features().includes(flag) ? true : router.parseUrl(fallback()));

        if (store.isReady()) {
            return evaluate();
        }

        return toObservable(store.isReady).pipe(
            filter((isReady) => isReady),
            take(1),
            map(() => evaluate()),
        );
    };
